import React, { useState, useEffect, useCallback } from 'react';
import './LifeNarrative.css';

function LifeNarrative({ isOpen, onClose, token, api }) {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState(null);
  const [style, setStyle] = useState('memoir');

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }), [token]);

  const flash = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchChapters = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${api}/narrative/chapters`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setChapters(data.chapters || []);
      }
    } catch (e) { console.error('Failed to fetch chapters:', e); }
    setLoading(false);
  }, [api, headers]);

  useEffect(() => {
    if (isOpen && token) fetchChapters();
  }, [isOpen, token, fetchChapters]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${api}/narrative/generate`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ style }),
      });
      if (res.ok) {
        const data = await res.json();
        setChapters(data.chapters || []);
        flash(`Generated ${data.total} chapters`);
      } else {
        flash('Generation failed', 'error');
      }
    } catch { flash('Generation failed', 'error'); }
    setGenerating(false);
  };

  const handleRegenerate = async (chapterId) => {
    setRegeneratingId(chapterId);
    try {
      const res = await fetch(`${api}/narrative/chapters/${chapterId}/regenerate`, {
        method: 'POST',
        headers: headers(),
      });
      if (res.ok) {
        const updated = await res.json();
        setChapters(prev => prev.map(ch => ch.id === chapterId ? updated : ch));
        flash('Chapter regenerated');
      } else {
        flash('Regeneration failed', 'error');
      }
    } catch { flash('Regeneration failed', 'error'); }
    setRegeneratingId(null);
  };

  const startEdit = (ch) => {
    setEditingId(ch.id);
    setEditContent(ch.content);
    setEditTitle(ch.title);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent('');
    setEditTitle('');
  };

  const saveEdit = async () => {
    try {
      const res = await fetch(`${api}/narrative/chapters/${editingId}`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ content: editContent, title: editTitle }),
      });
      if (res.ok) {
        const updated = await res.json();
        setChapters(prev => prev.map(ch => ch.id === editingId ? updated : ch));
        flash('Chapter saved');
        cancelEdit();
      } else {
        flash('Save failed', 'error');
      }
    } catch { flash('Save failed', 'error'); }
  };

  const handleFinalize = async (chapterId, finalized) => {
    try {
      const res = await fetch(`${api}/narrative/chapters/${chapterId}`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ is_finalized: finalized }),
      });
      if (res.ok) {
        const updated = await res.json();
        setChapters(prev => prev.map(ch => ch.id === chapterId ? updated : ch));
        flash(finalized ? 'Chapter approved' : 'Chapter unlocked');
      }
    } catch { flash('Update failed', 'error'); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${api}/narrative/export`, { headers: headers() });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `life-narrative-${new Date().toISOString().slice(0, 10)}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      flash('Download started');
    } catch { flash('Export failed', 'error'); }
    setExporting(false);
  };

  if (!isOpen) return null;

  const allFinalized = chapters.length > 0 && chapters.every(ch => ch.is_finalized);

  return (
    <div className="ln-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ln-panel">
        {toast && <div className={`ln-toast ${toast.type}`}>{toast.text}</div>}

        {/* Branded Header */}
        <div className="ln-header-banner">
          <div className="ln-header-top">
            <div className="ln-brand">
              <div className="ln-brand-icon">📖</div>
              <div>
                <h2 className="ln-title">Life Narrative</h2>
                <div className="ln-title-sub">Heart Felt Souls</div>
              </div>
            </div>
            <button className="ln-close" onClick={onClose}>&times;</button>
          </div>
          <p className="ln-subtitle">
            Your AI-crafted life story, woven from memories, soul profile, and wisdom.
          </p>
        </div>

        {/* Controls */}
        <div className="ln-controls">
          <div className="ln-style-picker">
            <label>Style:</label>
            <select value={style} onChange={(e) => setStyle(e.target.value)}>
              <option value="memoir">Memoir</option>
              <option value="literary">Literary</option>
              <option value="conversational">Conversational</option>
              <option value="poetic">Poetic</option>
            </select>
          </div>
          <div className="ln-actions">
            <button className="ln-generate-btn" onClick={handleGenerate} disabled={generating}>
              {generating ? 'Writing…' : chapters.length > 0 ? 'Regenerate All' : 'Generate My Narrative'}
            </button>
            {chapters.length > 0 && (
              <button className="ln-export-btn" onClick={handleExport} disabled={exporting}>
                {exporting ? 'Exporting…' : 'Export as Text'}
              </button>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="ln-content-area">
          {generating && (
            <div className="ln-generating">
              <div className="ln-generating-orb" />
              <div className="ln-generating-text">Crafting your life story…</div>
              <div className="ln-generating-hint">This may take a minute as we weave your memories together.</div>
            </div>
          )}

          {loading && !generating && (
            <div className="ln-loading">Loading your narrative…</div>
          )}

          {/* Empty State */}
          {!generating && chapters.length === 0 && !loading && (
            <div className="ln-empty">
              <div className="ln-empty-visual">✦</div>
              <div className="ln-empty-title">Your story awaits</div>
              <p>Click <strong>Generate My Narrative</strong> to create a personalized life story from your memories and soul profile.</p>
            </div>
          )}

          {/* Chapters */}
          {!generating && chapters.length > 0 && (
            <div className="ln-chapters">
              {chapters.map(ch => {
                const isExpanded = expandedId === ch.id;
                const isEditing = editingId === ch.id;
                const isRegenerating = regeneratingId === ch.id;

                return (
                  <div key={ch.id} className={`ln-chapter ${ch.is_finalized ? 'ln-finalized' : ''} ${isExpanded ? 'ln-expanded' : ''}`}>
                    <div className="ln-chapter-header" onClick={() => setExpandedId(isExpanded ? null : ch.id)}>
                      <div className="ln-chapter-number">Ch. {ch.chapter_number}</div>
                      <div className="ln-chapter-title">{ch.title}</div>
                      <div className="ln-chapter-badges">
                        {ch.is_finalized && <span className="ln-badge ln-badge-approved">Approved</span>}
                        <span className="ln-expand-icon">{isExpanded ? '▾' : '▸'}</span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="ln-chapter-body">
                        {isEditing ? (
                          <div className="ln-edit-form">
                            <input
                              className="ln-edit-title"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              placeholder="Chapter title"
                            />
                            <textarea
                              className="ln-edit-content"
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              rows={12}
                            />
                            <div className="ln-edit-actions">
                              <button className="ln-save-btn" onClick={saveEdit}>Save</button>
                              <button className="ln-cancel-btn" onClick={cancelEdit}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="ln-chapter-content">
                              {ch.content.split('\n').map((p, i) => p.trim() ? <p key={i}>{p}</p> : null)}
                            </div>
                            <div className="ln-chapter-actions">
                              <button className="ln-action-btn" onClick={() => startEdit(ch)} title="Edit">
                                ✎ Edit
                              </button>
                              <button
                                className="ln-action-btn"
                                onClick={() => handleRegenerate(ch.id)}
                                disabled={isRegenerating}
                                title="Regenerate"
                              >
                                {isRegenerating ? '⟳ …' : '⟳ Regenerate'}
                              </button>
                              <button
                                className={`ln-action-btn ${ch.is_finalized ? 'ln-unlock' : 'ln-approve'}`}
                                onClick={() => handleFinalize(ch.id, !ch.is_finalized)}
                              >
                                {ch.is_finalized ? '🔓 Unlock' : '✓ Approve'}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {allFinalized && (
                <div className="ln-all-approved">
                  ✦ All chapters approved — your narrative is ready to export and share.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="ln-footer">
          Powered by <span className="ln-footer-brand">Heart Felt Souls</span> — your memories, your story.
        </div>
      </div>
    </div>
  );
}

export default LifeNarrative;
